import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as verifyFormActions from '~src/domains/verify/state/verifyFormReducer';
import * as styles from '~src/styles/domains/verify/VerifyPage.module.scss';
import { updateFormMeta } from '~src/utils/forms';
import { applyMask } from '~src/utils/helpers';
import { trackFocus, trackPageMount } from '~src/utils/tracking';
import { validate } from '~src/utils/validation';
import { xhrNext } from '~src/utils/xhr';
import Alerter from '../common/components/Alerter';
import SimpleButton from '../common/components/Button';
import InputGroup from '../common/components/InputGroup';
import LinkWrapper from '../common/components/LinkWrapper';
import { Endpoint, Page } from '../common/enums/api';
import { AlertNames, VerifyFormNames } from '../common/enums/common';
import {
  brandSelector,
  customerInfoSelector,
  localeSelector,
  mcodeSelector,
} from '../common/state/commonSelectors';
import { startFormSelector } from '../start/state/startSelectors';
import { setShowResendLink } from './state/verifyReducer';
import {
  showResendLinkSelector,
  verifyFormSelector,
} from './state/verifySelectors';

const VerifyPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const brand = useSelector(brandSelector);
  const { phone } = useSelector(customerInfoSelector);
  const verifyForm = useSelector(verifyFormSelector) as {
    [key in VerifyFormNames]: any;
  };
  const showResendLink = useSelector(showResendLinkSelector);
  const locale = useSelector(localeSelector);
  const mcode = useSelector(mcodeSelector);
  const startForm = useSelector(startFormSelector);
  const phoneNumber = (startForm?.mobile?.value as string) || phone;
  const formattedPhoneNumber =
    phoneNumber && applyMask(phoneNumber, '999-999-9999');
  const inputs = t('verify:inputs', {
    returnObjects: true,
  }) as verifyFormActions.VerifyLocalization;

  /** Function to show SMS resend link. */
  const smsDidNotReceive = () => {
    dispatch(setShowResendLink(true));

    setTimeout(() => {
      const verificationCodeElement = document.getElementById(
        VerifyFormNames.MOBILE_VERIFICATION_CODE,
      );
      verificationCodeElement?.focus();
    }, 500);
  };

  /** Force an XHR request to /apply, which will:
   * 1) Resend the verification code
   * 2) Reload the Verify page
   * */
  const smsResend = () => {
    dispatch(
      xhrNext({
        forceNext: Endpoint.APPLY,
        resending: true,
      }),
    );

    setTimeout(() => {
      dispatch(setShowResendLink(false));
    }, 100);
  };

  const smsCodeResend = showResendLink ? (
    <div className={styles['sms-resend-now-wrapper']}>
      <span>{t('verify:sms.check-mobile')} </span>
      <LinkWrapper
        id='sms-resend-now'
        aria-live='assertive'
        onClickHandler={smsResend}
        trackingConfig={{
          label: t('verify:sms.resend-now'),
        }}
      >
        <span class='text-nowrap'>{t('verify:sms.resend-now')}</span>
      </LinkWrapper>
    </div>
  ) : (
    <LinkWrapper
      id={styles['sms-resend-code']}
      onClickHandler={smsDidNotReceive}
      trackingConfig={{ label: t('verify:sms.resend-code') }}
    >
      {t('verify:sms.resend-code')}
    </LinkWrapper>
  );

  const submitForm = () => {
    const errors = validate(verifyForm);

    errors.forEach((error, idx: number) => {
      if (idx === 0) {
        const errorElement = document.getElementById(error.name);
        errorElement?.focus();
      }
      dispatch(
        verifyFormActions.updateInputIsValid({
          name: error.name,
          msg: error.errorMsg,
        }),
      );
    });

    batch(() => {
      if (!errors.length) {
        dispatch(xhrNext());
      } else {
        dispatch(trackFocus(['verifyForm', errors[0].name, 'value']));
      }
    });
  };

  useEffect(() => {
    dispatch(trackPageMount(Page.VERIFY));
  }, []);

  useEffect(() => {
    if (typeof inputs === 'string') {
      return;
    }
    dispatch(updateFormMeta(inputs, verifyFormActions));
  }, [locale]);

  return (
    <div className={styles['verify-wrapper']}>
      {mcode != undefined && (
        <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
      )}
      <h1
        className={`header-txt ${styles['verify-header']} ${
          mcode != undefined ? styles.error : ''
        }`}
      >
        {t('verify:header')}
      </h1>
      <div>
        <div>
          <InputGroup
            page={Page.VERIFY}
            input={{
              ...inputs[VerifyFormNames.MOBILE_VERIFICATION_CODE],
              label: (
                <Trans
                  t={t}
                  i18nKey={`verify:sms.${brand.toLowerCase()}.label`}
                  values={{ phoneNumber: formattedPhoneNumber }}
                />
              ),
            }}
            id={VerifyFormNames.MOBILE_VERIFICATION_CODE}
            state={verifyForm[VerifyFormNames.MOBILE_VERIFICATION_CODE]}
            centered={true}
            reduxActions={verifyFormActions}
          />
        </div>
        <div className={styles['sms-resend']}>{smsCodeResend}</div>
      </div>
      <SimpleButton
        label={t('verify:submit')}
        trackingLabel={Page.VERIFY}
        onClickPost={submitForm}
        classLabel={`btn-primary ${styles['verify-form-submit']}`}
        trackClickPayload={{
          label: t('verify:submit'),
        }}
      />
    </div>
  );
};

export default VerifyPage;
